import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { useI18n } from '../lib/i18n';
import Icon from './Icon';
import { formatRSD, formatCur } from '../lib/helpers';

// ============================================================
// POŠALJI FAKTURU MEJLOM
// PDF sa pravim tekstom pravi InvoicePdf.js (prosleđen kao buildPdf),
// a šalje ga Supabase funkcija "send-invoice" preko Resend-a (fakture@finart.rs).
// ============================================================

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const splitEmails = (v) => String(v || '').split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);

// Podrazumevana poruka — na jeziku fakture (srpski / engleski / ruski)
function defaultTexts(dl, { kindWord, no, amount, firm }) {
  const k = kindWord.toLowerCase();
  if (dl === 'en') {
    return {
      subject: `${kindWord} ${no} – ${firm}`,
      message: `Dear Sir or Madam,\n\nplease find attached ${k} no. ${no} for the services provided, in the amount of ${amount}.\n\nShould you have any questions regarding the invoiced services or amounts, please do not hesitate to contact us.\n\nThank you for your trust. We look forward to continuing our successful cooperation.`,
    };
  }
  if (dl === 'ru') {
    return {
      subject: `${kindWord} ${no} – ${firm}`,
      message: `Уважаемые господа,\n\nво вложении ${k} № ${no} за оказанные услуги на сумму ${amount}.\n\nЕсли у вас возникнут вопросы по услугам или суммам в счёте, пожалуйста, свяжитесь с нами.\n\nБлагодарим за доверие и рассчитываем на дальнейшее успешное сотрудничество.`,
    };
  }
  return {
    subject: `${kindWord} ${no} – ${firm}`,
    message: `Poštovani,\n\nu prilogu se nalazi ${k} br. ${no} za pružene usluge, na iznos od ${amount}.\n\nUkoliko imate bilo kakvih pitanja ili nejasnoća u vezi sa fakturisanom uslugom ili iznosom, slobodno nas kontaktirajte.\n\nHvala Vam na ukazanom poverenju. Radujemo se nastavku uspešne poslovne saradnje.`,
  };
}

const KIND_WORDS = {
  sr: { faktura: 'Faktura', profaktura: 'Profaktura', fiskalni: 'Račun' },
  en: { faktura: 'Invoice', profaktura: 'Proforma invoice', fiskalni: 'Receipt' },
  ru: { faktura: 'Счёт', profaktura: 'Проформа', fiskalni: 'Чек' },
};

export default function SendInvoiceDialog({ invoice, issuer, customer, buildPdf, dl = 'sr', onClose, onSent }) {
  const { user } = useAuth();
  const { tr } = useI18n();
  const isRsd = invoice.type === 'domaca' || invoice.document_kind === 'fiskalni';
  const amount = isRsd ? formatRSD(invoice.amount) : formatCur(invoice.amount, invoice.currency);
  const firm = (invoice.issuer_name_mode === 'short' && issuer?.shortName ? issuer.shortName : issuer?.name) || '';
  const kindWord = (KIND_WORDS[dl] || KIND_WORDS.sr)[invoice.document_kind] || KIND_WORDS.sr.faktura;
  const defaults = defaultTexts(dl, { kindWord, no: invoice.invoice_number || '', amount, firm });

  const [to, setTo] = useState(customer?.email || '');
  const [ccMe, setCcMe] = useState(true);
  const [subject, setSubject] = useState(defaults.subject);
  const [message, setMessage] = useState(defaults.message);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const fileName = `${String(document.title || `Faktura ${invoice.invoice_number || ''}`).replace(/[\\/:*?"<>|]+/g, '-').trim()}.pdf`;


  const makePdf = async (output) => {
    if (!buildPdf) throw new Error('Faktura nije spremna za PDF.');
    const doc = await buildPdf();
    return doc.output(output);
  };

  const preview = async () => {
    setError('');
    setBusy('preview');
    try {
      const url = await makePdf('bloburl');
      window.open(url, '_blank');
    } catch (e) {
      setError(e.message || String(e));
    }
    setBusy('');
  };

  const send = async () => {
    setError('');
    const list = splitEmails(to);
    if (!list.length) { setError(tr('Upišite e-mail klijenta.', 'Enter the client e-mail.', 'Введите e-mail клиента.')); return; }
    const bad = list.find((e) => !EMAIL_RE.test(e));
    if (bad) { setError(tr(`Adresa nije ispravna: ${bad}`, `Invalid address: ${bad}`, `Неверный адрес: ${bad}`)); return; }
    if (list.length > 5) { setError(tr('Najviše 5 adresa.', 'At most 5 addresses.', 'Не более 5 адресов.')); return; }
    if (!subject.trim()) { setError(tr('Upišite naslov.', 'Enter a subject.', 'Введите тему.')); return; }

    setBusy('send');
    try {
      const dataUri = await makePdf('datauristring');
      const pdf = String(dataUri).split(',')[1];
      const { data, error: fnError } = await supabase.functions.invoke('send-invoice', {
        body: { invoice_id: invoice.id, to: list, cc_me: ccMe, subject: subject.trim(), message, pdf_base64: pdf, filename: fileName },
      });
      if (fnError) {
        let msg = fnError.message;
        try { const b = await fnError.context?.json(); if (b?.error) msg = b.error; } catch { /* ignore */ }
        throw new Error(msg || 'Slanje nije uspelo.');
      }
      if (!data || data.error) throw new Error(data?.error || 'Slanje nije uspelo.');

      // Zapamti adresu kod klijenta ako je nije imao
      if (customer?.id && !customer.email) {
        await supabase.from('customers').update({ email: list.join(', ') }).eq('id', customer.id);
      }
      setSent(true);
      if (onSent && data.invoice) onSent(data.invoice);
    } catch (e) {
      setError(e.message || String(e));
    }
    setBusy('');
  };

  // Zatvaranje samo na ✕, "Otkaži" ili Esc — klik pored prozora ga NE zatvara (da se ne izgubi uneto)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  return (
    <div style={s.overlay} className="no-print">
      <div style={s.modal} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div style={s.titleRow}>
          <span style={s.title}>{tr('Pošalji fakturu', 'Send invoice', 'Отправить счёт')}</span>
          <button onClick={onClose} disabled={!!busy} style={s.close} aria-label={tr('Zatvori', 'Close', 'Закрыть')}><Icon name="x" size={18} /></button>
        </div>
        <div style={s.docRow}>
          <span style={s.docNo}>{kindWord} {invoice.invoice_number}</span>
          <span style={s.docSub}>· {invoice.client || ''} · {amount}</span>
        </div>

        {sent ? (
          <div style={s.okBox}>
            <strong>{tr('Faktura je poslata.', 'The invoice has been sent.', 'Счёт отправлен.')}</strong>
            <span>{tr(`Primalac: ${splitEmails(to).join(', ')}`, `Recipient: ${splitEmails(to).join(', ')}`, `Получатель: ${splitEmails(to).join(', ')}`)}</span>
            <button onClick={onClose} style={{ ...s.primary, alignSelf: 'flex-end', marginTop: 8 }}>{tr('Zatvori', 'Close', 'Закрыть')}</button>
          </div>
        ) : (
          <>
            <label style={s.field}>
              <span style={s.label}>{tr('Za (e-mail klijenta)', 'To (client e-mail)', 'Кому (e-mail клиента)')}</span>
              <input type="email" multiple value={to} onChange={(e) => setTo(e.target.value)} placeholder="ime@firma.rs" style={s.input} autoFocus={!to} />
              <span style={s.hint}>{tr('Više adresa razdvojite zarezom.', 'Separate several addresses with commas.', 'Несколько адресов — через запятую.')}</span>
            </label>
            <label style={s.check}>
              <input type="checkbox" checked={ccMe} onChange={(e) => setCcMe(e.target.checked)} />
              {tr('Pošalji kopiju i meni', 'Send me a copy', 'Отправить копию мне')}{user?.email ? ` (${user.email})` : ''}
            </label>
            <label style={s.field}>
              <span style={s.label}>{tr('Naslov', 'Subject', 'Тема')}</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} style={s.input} />
            </label>
            <label style={s.field}>
              <span style={s.label}>{tr('Poruka', 'Message', 'Сообщение')}</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={7} style={{ ...s.input, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }} />
              <span style={s.hint}>{tr(`Na kraju se sam dodaje potpis: „S poštovanjem, ${firm}“.`, `A signature is added automatically: “Kind regards, ${firm}”.`, `Подпись добавится автоматически: «С уважением, ${firm}».`)}</span>
            </label>
            <div style={s.attach}>
              <span style={s.pdfIcon}>PDF</span>
              <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                <span style={s.attachName}>{fileName}</span>
                <span style={s.hint}>{tr('Prilog – ista faktura kao kad je preuzimate', 'Attachment – same as the downloaded invoice', 'Вложение — тот же счёт, что и при скачивании')}</span>
              </span>
              <button type="button" onClick={preview} disabled={!!busy} style={s.link}>{busy === 'preview' ? '...' : tr('Pogledaj', 'Preview', 'Просмотр')}</button>
            </div>

            {error && <div style={s.error}>{error}</div>}
            <div style={s.btnRow}>
              <button onClick={onClose} disabled={!!busy} style={s.cancel}>{tr('Otkaži', 'Cancel', 'Отмена')}</button>
              <button onClick={send} disabled={!!busy} style={s.primary}>
                {busy === 'send' ? tr('Šaljem...', 'Sending...', 'Отправка...') : tr('Pošalji', 'Send', 'Отправить')}
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
}

const s = {
  overlay: { position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(12,37,48,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, textAlign: 'left', cursor: 'default' },
  modal: { width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto', background: 'var(--surface)', borderRadius: 10, padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 12, boxShadow: '0 20px 50px rgba(12,37,48,0.25)' },
  titleRow: { display: 'flex', alignItems: 'center', gap: 10 },
  title: { flex: 1, fontSize: 19, fontWeight: 800, color: 'var(--ink-900)' },
  close: { width: 36, height: 36, border: 'none', background: 'transparent', color: 'var(--ink-500)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  docRow: { display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap', marginTop: -6 },
  docNo: { fontSize: 15, fontWeight: 800, color: 'var(--teal-800)' },
  docSub: { fontSize: 13, color: 'var(--ink-500)' },
  field: { display: 'flex', flexDirection: 'column', gap: 5 },
  label: { fontSize: 12.5, fontWeight: 700, color: 'var(--ink-700)' },
  input: { border: '1.5px solid var(--line)', borderRadius: 10, padding: '10px 12px', fontSize: 15, width: '100%', minHeight: 42, background: 'var(--surface)', boxSizing: 'border-box' },
  hint: { fontSize: 11.5, color: 'var(--ink-500)' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: 'var(--ink-700)' },
  attach: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--paper)', borderRadius: 8 },
  pdfIcon: { flexShrink: 0, width: 34, height: 40, borderRadius: 6, background: 'var(--red-100)', color: 'var(--red-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 },
  attachName: { fontSize: 13.5, fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  link: { border: 'none', background: 'none', color: 'var(--teal-800)', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  error: { background: 'var(--red-100)', color: 'var(--red-600)', borderRadius: 10, padding: '10px 12px', fontSize: 13, wordBreak: 'break-word' },
  okBox: { display: 'flex', flexDirection: 'column', gap: 6, background: 'var(--forest-100)', color: 'var(--forest-600)', borderRadius: 8, padding: 14, fontSize: 14 },
  btnRow: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 2 },
  cancel: { minHeight: 42, padding: '0 18px', border: '1px solid var(--line)', borderRadius: 8, background: 'var(--surface)', color: 'var(--teal-800)', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
  primary: { minHeight: 42, padding: '0 22px', border: 'none', borderRadius: 8, background: 'var(--teal-800)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' },
};
